import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { InvestmentAccount } from './useInvestments';

/**
 * Hook to fetch a single investment account by ID with investment type details
 * 
 * @param investmentId - The ID of the investment account to fetch
 * @returns Query result containing investment account with investment_type data
 * 
 * @example
 * const { data: account, isLoading, error } = useInvestmentAccountDetail('inv-123');
 */
export function useInvestmentAccountDetail(investmentId: string) {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['investment-account', investmentId, user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('investment_accounts' as any)
        .select('*, investment_type:investment_types(*), risk_rating:risk_ratings(*)')
        .eq('id', investmentId)
        .single();
      
      if (error) throw error;
      if (!data) throw new Error('Investment account not found');
      
      return data as unknown as InvestmentAccount;
    },
    enabled: !!user && !!investmentId,
  });
}
